import React, { useEffect, useState } from 'react';
import { getFirestore, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import { ThemeProvider, createTheme } from '@mui/material/styles';

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#013a64',
    },
    secondary: {
      main: '#4CAF50',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
});

const RequestHistoryScreen = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const firestore = getFirestore();
  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRequests = () => {
      const user = auth.currentUser;
      if (!user) {
        console.log('No user is currently signed in.');
        setLoading(false);
        return;
      }

      const userId = user.uid;
      const requestsRef = collection(firestore, `students/${userId}/requests`);
      const q = query(requestsRef, orderBy('createdAt', 'desc'));

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const fetchedRequests = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setRequests(fetchedRequests);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching requests:', error);
          setLoading(false);
        }
      );

      return unsubscribe;
    };

    const unsubscribe = fetchRequests();
    return () => unsubscribe && unsubscribe();
  }, [auth, firestore]);

  const handlePress = (request) => {
    navigate('/RequestDetail', { state: { request } });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircleIcon color="success" />;
      case 'Pending':
        return <PendingIcon color="warning" />;
      case 'Rejected':
        return <CancelIcon color="error" />;
      default:
        return <PendingIcon color="warning" />;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Convert "2:30 PM" to "14:30" for Date parsing
    const [time, modifier] = timeString.split(' ');
    let [hours, minutes] = time.split(':');
    
    if (hours === '12') {
      hours = '00';
    }
    
    if (modifier === 'PM') {
      hours = parseInt(hours, 10) + 12;
    }
    
    return `${hours}:${minutes}`;
  };

  const formatDateTime = (dateString, timeString) => {
    const formattedTime = formatTime(timeString);
    const date = new Date(`${dateString} ${formattedTime}`);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Header */}
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Back
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flex: 1, p: 3, bgcolor: '#f5f7fa' }}>
          <Paper elevation={0} sx={{ maxWidth: 800, mx: 'auto', p: 0 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress color="primary" />
              </Box>
            ) : requests.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No pass requests found
                </Typography>
              </Box>
            ) : (
              <List sx={{ width: '100%' }}>
                {requests.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        p: 2
                      }}
                      onClick={() => handlePress(item)}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getStatusIcon(item.status)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                              {formatDateTime(item.date, item.time)}
                            </Typography>
                            <Chip
                              label={item.status}
                              size="small"
                              icon={getStatusIcon(item.status)}
                              color={
                                item.status === 'Approved' ? 'success' :
                                item.status === 'Rejected' ? 'error' : 'warning'
                              }
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <DescriptionIcon color="action" sx={{ mr: 1, fontSize: 16 }} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: 'inline' }}
                              >
                                {item.purpose}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <EventIcon color="action" sx={{ mr: 1, fontSize: 16 }} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: 'inline', mr: 2 }}
                              >
                                {item.date}
                              </Typography>
                              <ScheduleIcon color="action" sx={{ mr: 1, fontSize: 16 }} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: 'inline' }}
                              >
                                {item.time}
                              </Typography>
                            </Box>
                          </>
                        }
                      />
                    </ListItem>
                    {index < requests.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default RequestHistoryScreen;